/**
 * The deploy.sh and destroy.sh a test deployment downloads, run on the engineer's own machine.
 *
 * They sign in AS THE ENGINEER and take the project's deployment identity for an hour (Andrew,
 * 22 Oct 2026: "replace the stored cloud keys with short-lived credentials"). VaultSentinel used to
 * hand them stored cloud keys; it now hands them only which identity to take - a role ARN, a
 * subscription, a service account to impersonate, an IBM account - and the cloud's own audit log
 * names the engineer on every call:
 *   aws    their own session (aws sso login), then sts:AssumeRole on the deployment role, 1 hour,
 *          with their name as session name and source identity
 *   azure  their own az login; Terraform uses the Azure CLI's tokens (ARM_USE_CLI), no client secret
 *   gcp    their own gcloud application-default login, impersonating a service account with no keys
 *   ibm    their own ibmcloud SSO login; Terraform gets the IAM token from it
 * Nothing is written to disk, and nothing secret is in the script.
 */
import type { IaCDeployment, IaCDeployTarget } from './api'

/** A value in single quotes for bash. The identity fields are validated on the server; this is belt and braces. */
export const sh = (v: string | number | null | undefined) => `'${String(v ?? '').replace(/'/g, `'\\''`)}'`

const PREAMBLE = (title: string, baseUrl: string, dep: IaCDeployment, mainId: string, generatedAt: string) => [
    '#!/bin/bash',
    '# ═══════════════════════════════════════════════════════════',
    `# VaultSentinel — ${title}`,
    '# Project: ' + mainId,
    '# Deployment ID: ' + dep.id,
    '# Generated: ' + generatedAt,
    '# No cloud secret is in this script: it signs in as you and takes the',
    '# deployment identity below for one hour.',
    '# ═══════════════════════════════════════════════════════════',
    'set -euo pipefail',
    '',
    '# ── Configuration ──',
    'VAULTSENTINEL_URL=' + sh(baseUrl),
    'DEPLOYMENT_ID=' + sh(dep.id),
    'DEPLOY_TOKEN=' + sh(dep.deployToken || ''),
    '',
    '# ── Helpers ──',
    'report_progress() {',
    '    local stage="$1" status="$2" message="$3" output="${4:-}" error_output="${5:-}"',
    '    # JSON-encode the output fields',
    '    local json_output json_error',
    '    json_output=$(echo -n "$output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
    '    json_error=$(echo -n "$error_output" | python3 -c \'import sys,json; print(json.dumps(sys.stdin.read()))\' 2>/dev/null || echo \'""\')',
    '    curl -s -X PUT "$VAULTSENTINEL_URL/api/iac/deployments/$DEPLOYMENT_ID/progress" \\',
    '        -H "Content-Type: application/json" \\',
    '        -H "x-deploy-token: $DEPLOY_TOKEN" \\',
    '        -d "{\\"stage\\": \\"$stage\\", \\"status\\": \\"$status\\", \\"message\\": \\"$message\\", \\"output\\": $json_output, \\"errorOutput\\": $json_error}" \\',
    '        > /dev/null 2>&1 || true',
    '}',
    '',
    'need() {',
    '    if ! command -v "$1" &> /dev/null; then',
    '        echo "ERROR: $1 is required. $2"',
    '        report_progress "credentials" "error" "$1 is not installed" "" "$1 is required. $2"',
    '        exit 1',
    '    fi',
    '}',
    '',
]

/**
 * Sign in as the engineer and take the deployment identity. Exports only what Terraform reads, for
 * this run only; nothing is written to disk.
 */
export function signInBlock(target: IaCDeployTarget, sessionName: string): string[] {
    const id = target.identity || {}
    const common = [
        '# ── Sign in: as you, then as the deployment identity for one hour ──',
        'STAGE="credentials"',
        'REGION=' + sh(target.region),
        'echo "═══ Signing in ═══"',
        'report_progress "credentials" "started" "Signing in as the engineer..."',
        'need python3 "Install Python 3."',
    ]
    if (target.cloudProvider === 'aws') {
        return [
            ...common,
            'AWS_ROLE_ARN=' + sh(id.roleArn),
            'SESSION_NAME=' + sh(sessionName),
            'SESSION_SECONDS="${SESSION_SECONDS:-3600}"   # the role allows up to its MaxSessionDuration',
            'need aws "Install the AWS CLI v2: https://aws.amazon.com/cli/"',
            'if ! aws sts get-caller-identity > /dev/null 2>&1; then',
            '    echo "Sign in to AWS as yourself first, for example:"',
            '    echo "    aws sso login --profile <your profile> && export AWS_PROFILE=<your profile>"',
            '    report_progress "credentials" "error" "Not signed in to AWS" "" "Run aws sso login, set AWS_PROFILE, and run deploy.sh again."',
            '    exit 1',
            'fi',
            'echo "Signed in as $(aws sts get-caller-identity --query Arn --output text)"',
            'ROLE_JSON=$(aws sts assume-role --role-arn "$AWS_ROLE_ARN" --role-session-name "$SESSION_NAME" \\',
            '    --source-identity "$SESSION_NAME" --duration-seconds "$SESSION_SECONDS" --output json) || {',
            '    report_progress "credentials" "error" "Could not take the deployment role" "" "sts:AssumeRole on $AWS_ROLE_ARN was refused for this sign-in. Ask for access to the role, or check the role\'s trust policy."',
            '    exit 1',
            '}',
            'eval "$(echo "$ROLE_JSON" | python3 -c \'import sys,json,shlex; c=json.load(sys.stdin)["Credentials"]; print("export AWS_ACCESS_KEY_ID=%s AWS_SECRET_ACCESS_KEY=%s AWS_SESSION_TOKEN=%s" % tuple(shlex.quote(c[k]) for k in ("AccessKeyId","SecretAccessKey","SessionToken")))\')"',
            'EXPIRES=$(echo "$ROLE_JSON" | python3 -c \'import sys,json; print(json.load(sys.stdin)["Credentials"]["Expiration"])\')',
            'unset AWS_PROFILE',
            'export AWS_REGION="$REGION" AWS_DEFAULT_REGION="$REGION"',
            'echo "✓ Using $AWS_ROLE_ARN as $SESSION_NAME until $EXPIRES"',
            'report_progress "credentials" "success" "Signed in to the deployment role until $EXPIRES"',
            '',
        ]
    }
    if (target.cloudProvider === 'azure') {
        return [
            ...common,
            'AZURE_TENANT_ID=' + sh(id.tenantId),
            'AZURE_SUBSCRIPTION_ID=' + sh(id.subscriptionId),
            'need az "Install the Azure CLI: https://learn.microsoft.com/cli/azure/install-azure-cli"',
            'if [ "$(az account show --query tenantId -o tsv 2>/dev/null || true)" != "$AZURE_TENANT_ID" ]; then',
            '    az login --tenant "$AZURE_TENANT_ID" > /dev/null',
            'fi',
            'az account set --subscription "$AZURE_SUBSCRIPTION_ID" || {',
            '    report_progress "credentials" "error" "No access to the subscription" "" "This sign-in cannot use subscription $AZURE_SUBSCRIPTION_ID. Ask to be added to its deployment group."',
            '    exit 1',
            '}',
            '# Terraform uses the Azure CLI\'s own short-lived tokens: no client secret anywhere.',
            'unset ARM_CLIENT_ID ARM_CLIENT_SECRET ARM_CLIENT_CERTIFICATE_PATH ARM_CLIENT_CERTIFICATE_PASSWORD',
            'export ARM_USE_CLI=true ARM_TENANT_ID="$AZURE_TENANT_ID" ARM_SUBSCRIPTION_ID="$AZURE_SUBSCRIPTION_ID"',
            'echo "✓ Using subscription $AZURE_SUBSCRIPTION_ID as $(az account show --query user.name -o tsv)"',
            'report_progress "credentials" "success" "Signed in to Azure as the engineer"',
            '',
        ]
    }
    if (target.cloudProvider === 'gcp') {
        return [
            ...common,
            'GCP_PROJECT_ID=' + sh(id.projectId),
            'GCP_SERVICE_ACCOUNT=' + sh(id.serviceAccount),
            'need gcloud "Install the Google Cloud CLI: https://cloud.google.com/sdk/docs/install"',
            '# Your own sign-in, never a key file.',
            'unset GOOGLE_APPLICATION_CREDENTIALS GOOGLE_CREDENTIALS',
            'if ! gcloud auth application-default print-access-token > /dev/null 2>&1; then',
            '    gcloud auth application-default login',
            'fi',
            'if ! gcloud auth print-access-token --impersonate-service-account="$GCP_SERVICE_ACCOUNT" > /dev/null 2>&1; then',
            '    report_progress "credentials" "error" "Cannot act as the deployment service account" "" "This sign-in needs roles/iam.serviceAccountTokenCreator on $GCP_SERVICE_ACCOUNT."',
            '    exit 1',
            'fi',
            'export GOOGLE_IMPERSONATE_SERVICE_ACCOUNT="$GCP_SERVICE_ACCOUNT" GOOGLE_PROJECT="$GCP_PROJECT_ID" GOOGLE_REGION="$REGION"',
            'echo "✓ Acting as $GCP_SERVICE_ACCOUNT (short-lived tokens, no key)"',
            'report_progress "credentials" "success" "Signed in to Google Cloud as the engineer, impersonating the deployment service account"',
            '',
        ]
    }
    return [
        ...common,
        'IBM_ACCOUNT_ID=' + sh(id.accountId),
        'IBM_RESOURCE_GROUP=' + sh(id.resourceGroup || ''),
        'need ibmcloud "Install the IBM Cloud CLI: https://cloud.ibm.com/docs/cli"',
        '# Your own SSO sign-in, never an API key.',
        'unset IC_API_KEY IBMCLOUD_API_KEY',
        'if ! ibmcloud iam oauth-tokens > /dev/null 2>&1; then',
        '    ibmcloud login --sso -a https://cloud.ibm.com -r "$REGION" -c "$IBM_ACCOUNT_ID"',
        'fi',
        'ibmcloud target -c "$IBM_ACCOUNT_ID" -r "$REGION" ${IBM_RESOURCE_GROUP:+-g "$IBM_RESOURCE_GROUP"} > /dev/null',
        'export IC_IAM_TOKEN=$(ibmcloud iam oauth-tokens --output json | python3 -c \'import sys,json; print(json.load(sys.stdin)["iam_token"].split(" ",1)[-1])\')',
        'IC_IAM_REFRESH_TOKEN=$(python3 -c \'import json,os; print(json.load(open(os.path.expanduser("~/.bluemix/config.json"))).get("IAMRefreshToken",""))\' 2>/dev/null || true)',
        'if [ -n "$IC_IAM_REFRESH_TOKEN" ]; then export IC_IAM_REFRESH_TOKEN; fi',
        'export IC_REGION="$REGION"',
        'echo "✓ Using IBM Cloud account $IBM_ACCOUNT_ID with your SSO session"',
        'report_progress "credentials" "success" "Signed in to IBM Cloud as the engineer"',
        '',
    ]
}

/** deploy.sh: sign in, then init, plan, and apply after the engineer confirms. */
export function deployScript(dep: IaCDeployment, opts: { baseUrl: string; mainId: string; generatedAt: string }): string {
    if (!dep.target || !dep.sessionName) throw new Error('The deployment has no identity to sign in to')
    return [
        ...PREAMBLE('Terraform Test Deployment', opts.baseUrl, dep, opts.mainId, opts.generatedAt),
        'cleanup_on_error() {',
        '    local exit_code=$?',
        '    if [ $exit_code -ne 0 ] && [ "${STAGE:-}" != "" ]; then',
        '        report_progress "$STAGE" "error" "Script failed with exit code $exit_code" "" "${ERROR_LOG:-Unknown error}"',
        '    fi',
        '}',
        'trap cleanup_on_error EXIT',
        '',
        'echo ""',
        'echo "╔══════════════════════════════════════════════════════════╗"',
        'echo "║  VaultSentinel — Terraform Test Deployment               ║"',
        'echo "║  Progress is reported back to VaultSentinel in real-time ║"',
        'echo "╚══════════════════════════════════════════════════════════╝"',
        'echo ""',
        '',
        '# ── Check prerequisites ──',
        'need terraform "Install from https://terraform.io"',
        'need curl "It reports progress back to VaultSentinel."',
        'echo "Terraform: $(terraform version | head -1)"',
        'echo ""',
        '',
        ...signInBlock(dep.target, dep.sessionName),
        '# ── Initialize Terraform ──',
        'STAGE="init"',
        'echo "═══ Step 1/3: Initializing Terraform ═══"',
        'report_progress "init" "started" "Running terraform init..."',
        '',
        'INIT_OUTPUT=""',
        'if INIT_OUTPUT=$(terraform init -no-color 2>&1); then',
        '    echo "✓ Terraform initialized"',
        '    report_progress "init" "success" "Terraform initialized successfully"',
        'else',
        '    echo "✗ Terraform init failed"',
        '    echo "$INIT_OUTPUT"',
        '    report_progress "init" "error" "Terraform init failed" "" "$INIT_OUTPUT"',
        '    ERROR_LOG="$INIT_OUTPUT"',
        '    exit 1',
        'fi',
        '',
        'echo ""',
        '',
        '# ── Plan ──',
        'STAGE="plan"',
        'echo "═══ Step 2/3: Planning Deployment ═══"',
        'report_progress "plan" "started" "Running terraform plan..."',
        '',
        'PLAN_OUTPUT=""',
        'if PLAN_OUTPUT=$(terraform plan -no-color -out=tfplan 2>&1); then',
        '    echo "✓ Terraform plan complete"',
        '    echo ""',
        '    echo "$PLAN_OUTPUT" | tail -20',
        '    report_progress "plan" "success" "Terraform plan complete" "$PLAN_OUTPUT"',
        'else',
        '    echo "✗ Terraform plan failed"',
        '    echo ""',
        '    echo "$PLAN_OUTPUT"',
        '    report_progress "plan" "error" "Terraform plan failed" "" "$PLAN_OUTPUT"',
        '    ERROR_LOG="$PLAN_OUTPUT"',
        '    exit 1',
        'fi',
        '',
        'echo ""',
        '',
        '# ── Apply ──',
        'STAGE="apply"',
        'echo "═══ Step 3/3: Applying Terraform ═══"',
        'read -p "Review the plan above. Apply changes? (yes/no): " CONFIRM',
        'if [ "$CONFIRM" != "yes" ]; then',
        '    report_progress "apply" "error" "Deployment cancelled by user" "" "User declined to apply the plan"',
        '    echo "Deployment cancelled."',
        '    exit 0',
        'fi',
        '',
        'echo ""',
        'echo "Applying..."',
        'report_progress "apply" "started" "Running terraform apply..."',
        '',
        'APPLY_OUTPUT=""',
        'if APPLY_OUTPUT=$(terraform apply -no-color tfplan 2>&1); then',
        '    echo "✓ Terraform apply complete!"',
        '    echo ""',
        '    report_progress "apply" "success" "Deployment successful" "$APPLY_OUTPUT"',
        '    echo "$APPLY_OUTPUT" | tail -10',
        '    echo ""',
        '    echo "═══════════════════════════════════════════════════"',
        '    echo "  ✓ DEPLOYMENT SUCCESSFUL"',
        '    echo "  Check VaultSentinel for status and details."',
        '    echo ""',
        '    echo "  To destroy when done testing:"',
        '    echo "    bash destroy.sh"',
        '    echo "═══════════════════════════════════════════════════"',
        'else',
        '    echo "✗ Terraform apply failed"',
        '    echo ""',
        '    echo "$APPLY_OUTPUT"',
        '    report_progress "apply" "error" "Terraform apply failed" "" "$APPLY_OUTPUT"',
        '    ERROR_LOG="$APPLY_OUTPUT"',
        '    echo ""',
        '    echo "═══════════════════════════════════════════════════"',
        '    echo "  ✗ DEPLOYMENT FAILED"',
        '    echo "  The error has been reported to VaultSentinel."',
        '    echo "  You can ask the IaC Agent to analyse and fix it."',
        '    echo "═══════════════════════════════════════════════════"',
        '    exit 1',
        'fi',
    ].join('\n')
}

/** destroy.sh: sign in the same way, then destroy after the engineer confirms. */
export function destroyScript(dep: IaCDeployment, opts: { baseUrl: string; mainId: string; generatedAt: string }): string {
    if (!dep.target || !dep.sessionName) throw new Error('The deployment has no identity to sign in to')
    return [
        ...PREAMBLE('Terraform Destroy Script', opts.baseUrl, dep, opts.mainId, opts.generatedAt),
        'echo ""',
        'echo "╔══════════════════════════════════════════════════════════╗"',
        'echo "║  VaultSentinel — Terraform DESTROY                       ║"',
        'echo "║  ⚠  This will remove ALL cloud resources                ║"',
        'echo "╚══════════════════════════════════════════════════════════╝"',
        'echo ""',
        '',
        'read -p "Are you sure you want to destroy all resources? (yes/no): " CONFIRM',
        'if [ "$CONFIRM" != "yes" ]; then',
        '    echo "Cancelled."',
        '    exit 0',
        'fi',
        '',
        'need terraform "Install from https://terraform.io"',
        ...signInBlock(dep.target, dep.sessionName),
        'echo ""',
        'echo "Destroying resources..."',
        'report_progress "destroy" "started" "Destroying cloud resources..."',
        '',
        'DESTROY_OUTPUT=""',
        'if DESTROY_OUTPUT=$(terraform destroy -auto-approve -no-color 2>&1); then',
        '    echo "✓ All resources destroyed"',
        '    report_progress "destroy" "success" "All resources destroyed" "$DESTROY_OUTPUT"',
        '    echo ""',
        '    echo "═══════════════════════════════════════════════════"',
        '    echo "  ✓ RESOURCES DESTROYED"',
        '    echo "═══════════════════════════════════════════════════"',
        'else',
        '    echo "✗ Destroy failed"',
        '    echo "$DESTROY_OUTPUT"',
        '    report_progress "destroy" "error" "Terraform destroy failed" "" "$DESTROY_OUTPUT"',
        '    exit 1',
        'fi',
    ].join('\n')
}
